// ─── MapEditorModal ──────────────────────────────────────────────────────────
// Popup that matches the XD "Map Editor" screen. Lets the user:
//   - Pick a pin colour for each site-type bucket (Land Based, Pond Buoy,
//     Weather Station)
//   - Choose the default map tile layer (Streets / Imagery / Topographic)
//   - Set the default zoom + centre (Lat/Lng numeric fields + click-on-map
//     preview)
//
// The modal operates on a draft copy of the project's saved map config; the
// caller persists it via onSave on confirm. Cancel discards the draft.
//
// Layout notes:
//   - Header pinned at top with Save + Cancel on the right (matches XD)
//   - Fullscreen fixed overlay with semi-opaque scrim
//   - Form vertically scrolls inside a card with a max width so it stays
//     readable on wide monitors
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { SITE_TYPES, TILE_LAYERS, getTileLayer, defaultMapConfig } from './mapConfig'

// react-leaflet's default Marker icon URLs are bundler-broken in Vite (they
// resolve at import time, not at runtime). Override with the CDN urls from
// the leaflet package so the default blue pin renders correctly.
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// Inner component that wires map clicks to update centre. Must be a child of
// MapContainer to access the map instance via the useMapEvents hook.
function CenterPicker({ lat, lng, onPick }) {
    useMapEvents({
        click(e) {
            onPick(e.latlng.lat, e.latlng.lng)
        },
    })
    if (lat == null || lng == null) return null
    return <Marker position={[lat, lng]} />
}

export default function MapEditorModal({ open, initialConfig, onSave, onCancel }) {
    const [draft, setDraft] = useState(() => initialConfig || defaultMapConfig())
    const fileInputRef = useRef(null)

    // Reset draft whenever the modal is (re)opened so cancelling and reopening
    // doesn't carry over unsaved edits.
    useEffect(() => {
        if (open) setDraft(initialConfig || defaultMapConfig())
    }, [open, initialConfig])

    // Escape closes the modal (cancel behaviour) — keeps parity with other
    // dashboard modals (Disclaimer, Description, Webcam editor).
    useEffect(() => {
        if (!open) return
        const onKey = (e) => { if (e.key === 'Escape') onCancel() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onCancel])

    if (!open) return null

    const setColor   = (k, v) => setDraft((d) => ({ ...d, pinColors: { ...d.pinColors, [k]: v } }))
    const setLayer   = (v)    => setDraft((d) => ({ ...d, tileLayer: v }))
    const setLat     = (v)    => setDraft((d) => ({ ...d, centerLat: v }))
    const setLng     = (v)    => setDraft((d) => ({ ...d, centerLng: v }))
    const setZoom    = (v)    => setDraft((d) => ({ ...d, zoom: v }))

    // Where the preview map should centre. Falls back to a global view (lat 20,
    // lng 0) if the user hasn't picked a centre yet so the map still renders.
    const previewLat  = Number.isFinite(draft.centerLat) ? draft.centerLat : 20
    const previewLng  = Number.isFinite(draft.centerLng) ? draft.centerLng : 0
    const previewZoom = Number.isFinite(draft.zoom) ? draft.zoom : 2
    const tile = getTileLayer(draft.tileLayer)

    const onLatChange = (e) => {
        const v = e.target.value
        if (v === '') return setLat(null)
        const n = parseFloat(v)
        if (Number.isFinite(n) && n >= -90 && n <= 90) setLat(n)
    }
    const onLngChange = (e) => {
        const v = e.target.value
        if (v === '') return setLng(null)
        const n = parseFloat(v)
        if (Number.isFinite(n) && n >= -180 && n <= 180) setLng(n)
    }

    return (
        <div
            className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-center overflow-y-auto py-8"
            onClick={onCancel}
        >
            <div
                className="bg-white w-full max-w-md rounded shadow-lg mx-4 my-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b">
                    <h2 className="text-base font-semibold text-gray-800">Map Editor</h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onSave(draft)}
                            className="px-3 py-1 text-sm bg-[#358ECD] text-white rounded hover:bg-[#2c7ab3]"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-5">
                    {/* Pin Settings ─────────────────────────────────────── */}
                    <section>
                        <h3 className="text-sm font-semibold text-gray-800 mb-3">Pin Settings</h3>
                        <div className="space-y-3">
                            {SITE_TYPES.map((t) => (
                                <div key={t.key} className="border-b border-gray-100 pb-3 last:border-b-0">
                                    <div className="text-sm font-semibold text-gray-700 mb-1.5">{t.label}</div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-gray-500">Pin Color</span>
                                        <div className="relative">
                                            <input
                                                type="color"
                                                value={draft.pinColors[t.key] || t.defaultColor}
                                                onChange={(e) => setColor(t.key, e.target.value)}
                                                className="w-7 h-7 rounded-full border border-gray-200 cursor-pointer p-0"
                                                aria-label={`${t.label} pin color`}
                                                style={{ appearance: 'none' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Default Map Type ─────────────────────────────────── */}
                    <section>
                        <h3 className="text-sm font-semibold text-gray-800 mb-2">Default Map Type</h3>
                        <select
                            value={draft.tileLayer}
                            onChange={(e) => setLayer(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30"
                        >
                            {TILE_LAYERS.map((l) => (
                                <option key={l.key} value={l.key}>{l.label}</option>
                            ))}
                        </select>
                    </section>

                    {/* Default Zoom ─────────────────────────────────────── */}
                    <section>
                        <h3 className="text-sm font-semibold text-gray-800 mb-2">Default Zoom</h3>
                        <div className="rounded overflow-hidden border border-gray-200 mb-2" style={{ height: 200 }}>
                            <MapContainer
                                key={`${tile.key}-${previewLat}-${previewLng}`}
                                center={[previewLat, previewLng]}
                                zoom={previewZoom}
                                style={{ width: '100%', height: '100%' }}
                                scrollWheelZoom
                            >
                                <TileLayer
                                    url={tile.url}
                                    attribution={tile.attribution}
                                    maxZoom={tile.maxZoom}
                                />
                                <CenterPicker
                                    lat={Number.isFinite(draft.centerLat) ? draft.centerLat : null}
                                    lng={Number.isFinite(draft.centerLng) ? draft.centerLng : null}
                                    onPick={(la, ln) => { setLat(la); setLng(ln) }}
                                />
                            </MapContainer>
                        </div>
                        <p className="text-[11px] text-gray-500 mb-3">
                            Type or click the map to set center coordinates.
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Latitude</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={draft.centerLat ?? ''}
                                    onChange={onLatChange}
                                    placeholder="—"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-600 mb-1">Longitude</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={draft.centerLng ?? ''}
                                    onChange={onLngChange}
                                    placeholder="—"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#358ECD]/30"
                                />
                            </div>
                        </div>

                        <div className="mt-3">
                            <label className="block text-xs text-gray-600 mb-1">Zoom level ({draft.zoom})</label>
                            <input
                                type="range"
                                min="1"
                                max={tile.maxZoom || 19}
                                value={draft.zoom || 8}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full"
                            />
                        </div>
                    </section>
                </div>
            </div>
        </div>
    )
}
